import React, { useCallback, useState } from "react";
import { Box, ButtonBase, useTheme } from "@mui/material";
import { CalendarMonth } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type {
  Dashboard2DayBookingSummary,
  DayWorkStatus
} from "../types/dashboard2-work-status.types";
import {
  dashboard2WeekDayBookingOverflowPillSx,
  dashboard2WeekDayBookingPillSx,
  dashboard2WeekDayBookingPillsRowSx,
  dashboard2WeekDayBookingsRowSx,
  dashboard2WeekDayIconRailSx
} from "../dashboard2-status.styles";
import {
  deriveDayBookingPills,
  splitVisibleBookingPills,
  type WeekDayBookingPill
} from "../utils/dashboard2-week-day-card.utils";
import { Dashboard2DayBookingsDialog } from "./Dashboard2DayBookingsDialog";
import { Dashboard2WeekDayBookingPillIcon } from "./Dashboard2WeekDayBookingPillIcon";

type Dashboard2WeekDayBookingPillsProps = {
  dayLabel: string;
  status: DayWorkStatus;
  onBookingClick?: (booking: Dashboard2DayBookingSummary) => void;
};

const BookingPill: React.FC<{
  pill: WeekDayBookingPill;
  booking: Dashboard2DayBookingSummary;
  onBookingClick?: (booking: Dashboard2DayBookingSummary) => void;
}> = ({ pill, booking, onBookingClick }) => {
  const theme = useTheme();

  const content = (
    <>
      <Dashboard2WeekDayBookingPillIcon booking={booking} />
      <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
        {pill.label}
      </Box>
    </>
  );

  if (onBookingClick) {
    return (
      <Box
        component="button"
        type="button"
        sx={dashboard2WeekDayBookingPillSx(theme)}
        data-testid="dashboard2-week-day-booking-pill"
        onClick={event => {
          event.stopPropagation();
          onBookingClick(booking);
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Box
      component="span"
      sx={dashboard2WeekDayBookingPillSx(theme)}
      data-testid="dashboard2-week-day-booking-pill"
    >
      {content}
    </Box>
  );
};

export const Dashboard2WeekDayBookingPills: React.FC<Dashboard2WeekDayBookingPillsProps> = ({
  dayLabel,
  status,
  onBookingClick
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const pills = deriveDayBookingPills(status);
  const { visible, overflowCount } = splitVisibleBookingPills(pills);
  const hasBookingDetails = (status.additionalBookings?.length ?? 0) > 0;

  const handleOpenDialog = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  if (pills.length === 0) {
    return null;
  }

  return (
    <>
      <Box sx={dashboard2WeekDayBookingsRowSx} data-testid="dashboard2-week-day-bookings-row">
        <Box sx={dashboard2WeekDayIconRailSx} aria-hidden>
          <CalendarMonth sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={dashboard2WeekDayBookingPillsRowSx}>
          {visible.map(pill => {
            const booking = status.additionalBookings?.find(item => item.id === pill.id);

            if (!booking) {
              return null;
            }

            return (
              <BookingPill
                key={pill.id}
                pill={pill}
                booking={booking}
                onBookingClick={onBookingClick}
              />
            );
          })}
          {overflowCount > 0 && (
            <ButtonBase
              sx={dashboard2WeekDayBookingOverflowPillSx(theme)}
              onClick={hasBookingDetails ? handleOpenDialog : undefined}
              aria-label={t("_dashboard2_weekExtraBookingsOpen", {
                day: dayLabel,
                count: overflowCount
              })}
              data-testid="dashboard2-week-extra-bookings"
            >
              {t("_dashboard2_bookingPillOverflow", { count: overflowCount })}
            </ButtonBase>
          )}
        </Box>
      </Box>
      {hasBookingDetails && (
        <Dashboard2DayBookingsDialog
          open={dialogOpen}
          dayLabel={dayLabel}
          status={status}
          onClose={handleCloseDialog}
          onBookingClick={onBookingClick}
        />
      )}
    </>
  );
};
